import _ from 'underscore';
import Backbone from "backbone";

const BreathingAnalysisHelper = Backbone.Model.extend({
    idAttribute: "id",

    constructor: function(attributes, options) {
        Backbone.Model.apply(this, [attributes, options]);
    },

    /**
     * Simple, reliable breathing pause detection using only proven VAD + duration heuristics.
     * 
     * CONSERVATIVE APPROACH:
     * - Uses reliable Silero VAD results (proven to work)
     * - Simple duration-based classification (conservative thresholds)
     * - No complex analysis that could create false positives
     * - Clear labeling that this is heuristic-based, not definitive
     * 
     * @param {Object} vadResults - Cached VAD results with silenceSegments
     * @param {number} startTimeSeconds - Start time of the region to analyze (in seconds)
     * @param {number} endTimeSeconds - End time of the region to analyze (in seconds)
     * @param {Object} options - Detection options
     * @returns {Object} Conservative breathing pause analysis
     */
    findPotentialBreathingPauses : function(vadResults, startTimeSeconds, endTimeSeconds, options = {}) {
        const {
            minBreathDuration = 400,        // Conservative: longer minimum
            maxBreathDuration = 2000,       // Reasonable maximum
            conservativeMode = true         // Use conservative classification
        } = options;

        if (!vadResults || !vadResults.silenceSegments) {
            return {
                totalPotentialBreaths: 0,
                potentialBreaths: [],
                analysisMethod: 'conservative_vad_duration',
                note: 'No cached VAD results available'
            };
        }

        // Filter silence segments within our time range
        const relevantSilenceSegments = vadResults.silenceSegments.filter(silence => {
            return (silence.start < endTimeSeconds && silence.end > startTimeSeconds);
        });

        const potentialBreaths = [];
        
        relevantSilenceSegments.forEach(silence => {
            const duration = (silence.end - silence.start) * 1000; // Convert to ms
            
            if (duration >= minBreathDuration && duration <= maxBreathDuration) {
                // Conservative classification - only label as "potential"
                let classification = 'potential_pause';
                let likelihood = 'low';
                
                if (conservativeMode) {
                    // Very conservative thresholds
                    if (duration > 800) {
                        classification = 'likely_breathing_pause';
                        likelihood = 'medium';
                    } else if (duration > 600) {
                        classification = 'possible_breathing_pause';
                        likelihood = 'low-medium';
                    }
                } else {
                    // Slightly more permissive
                    if (duration > 600) {
                        classification = 'likely_breathing_pause';
                        likelihood = 'medium';
                    } else if (duration > 400) {
                        classification = 'possible_breathing_pause';
                        likelihood = 'low-medium';
                    }
                }
                
                potentialBreaths.push({
                    timestamp: silence.start + (silence.end - silence.start) / 2,
                    startTime: silence.start,
                    endTime: silence.end,
                    duration: duration,
                    classification: classification,
                    likelihood: likelihood,
                    note: 'Based on duration heuristics only - not definitive'
                });
            }
        });

        return {
            timeRange: { start: startTimeSeconds, end: endTimeSeconds },
            totalPotentialBreaths: potentialBreaths.length,
            potentialBreaths: potentialBreaths,
            analysisMethod: 'conservative_vad_duration',
            disclaimer: 'Classifications are heuristic-based and may include false positives'
        };
    },

    /**
     * Check for potential breathing pauses in a time range using VAD detection results.
     * Follows the same elegant pattern as VoiceActivityDetectionHelper.containsVoiceActivity().
     * 
     * @param {Array} voiceActivityDetectionResultArray - VAD results array (same format as containsVoiceActivity)
     * @param {number} startTimeInMilliseconds - Start time in milliseconds
     * @param {number} endTimeInMilliseconds - End time in milliseconds
     * @param {Object} options - Detection options
     * @returns {Object|null} Conservative potential breathing analysis
     */
    findPotentialBreathingPausesFromVADArray : function(voiceActivityDetectionResultArray, startTimeInMilliseconds, endTimeInMilliseconds, options = {}) {
        if (!voiceActivityDetectionResultArray) return null;
        
        // Convert VAD array to the format expected by findPotentialBreathingPauses
        const VoiceActivityDetectionHelper = require('models/audioEditor/VoiceActivityDetectionHelper').default;
        const vadResults = {
            silenceSegments: VoiceActivityDetectionHelper.getInstance().calculateSilenceSegments(
                voiceActivityDetectionResultArray, 
                endTimeInMilliseconds / 1000
            )
        };
        
        return this.findPotentialBreathingPauses(
            vadResults,
            startTimeInMilliseconds / 1000,
            endTimeInMilliseconds / 1000,
            options
        );
    },

    /**
     * Enhanced breathing pause detection with more detailed analysis.
     * This is the more sophisticated version that can be optionally enabled.
     * 
     * @param {Object} vadResults - Cached VAD results from getVoiceActivityDetectionResultsForAudioUrl
     * @param {number} startTimeSeconds - Start time of the region to analyze (in seconds)
     * @param {number} endTimeSeconds - End time of the region to analyze (in seconds)
     * @param {Object} options - Detection options
     * @returns {Object} Enhanced breathing pause analysis results
     */
    analyzeBreathingPausesFromVAD : function(vadResults, startTimeSeconds, endTimeSeconds, options = {}) {
        const {
            minBreathDuration = 300,        // Minimum breath duration in ms
            maxBreathDuration = 2000,       // Maximum breath duration in ms  
            breathConfidenceThreshold = 0.75 // Confidence threshold for breath classification
        } = options;

        if (!vadResults || !vadResults.silenceSegments) {
            return {
                totalBreathingPauses: 0,
                averageBreathDuration: 0,
                breathingPauses: [],
                breathingRate: 0,
                recommendations: [{
                    type: 'no_vad_data',
                    message: 'No cached VAD results available for breathing analysis'
                }]
            };
        }

        // Filter silence segments within our time range
        const relevantSilenceSegments = vadResults.silenceSegments.filter(silence => {
            const silenceStart = silence.start;
            const silenceEnd = silence.end;
            
            // Include segments that overlap with our time range
            return (silenceStart < endTimeSeconds && silenceEnd > startTimeSeconds);
        });

        // Analyze each silence segment for breathing characteristics
        const breathingPauses = [];
        
        relevantSilenceSegments.forEach(silence => {
            const duration = (silence.end - silence.start) * 1000; // Convert to ms
            
            // Only consider silences within our duration thresholds
            if (duration >= minBreathDuration && duration <= maxBreathDuration) {
                // Enhanced breathing pause classification
                let reason = 'natural_pause';
                let breathingConfidence = 0;
                
                if (duration > 300) {
                    // Likely breathing pause - analyze further
                    if (duration > 800) {
                        reason = 'long_breathing_pause';
                        breathingConfidence = 0.95;
                    } else if (duration > 500) {
                        reason = 'breathing_pause';
                        breathingConfidence = 0.85;
                    } else {
                        reason = 'short_breathing_pause';
                        breathingConfidence = 0.75;
                    }
                } else if (duration > 150) {
                    // Could be end-of-sentence pause or quick breath
                    reason = 'speech_pause';
                    breathingConfidence = 0.6;
                }
                
                // Only include if it meets our confidence threshold
                if (breathingConfidence >= breathConfidenceThreshold) {
                    breathingPauses.push({
                        timestamp: silence.start + (silence.end - silence.start) / 2, // Middle of silence
                        startTime: silence.start,
                        endTime: silence.end,
                        duration: duration,
                        confidence: breathingConfidence,
                        reason: reason,
                        isBreathingPause: breathingConfidence > 0.7,
                        cutQuality: duration > 500 ? 'excellent' : duration > 300 ? 'good' : 'acceptable'
                    });
                }
            }
        });

        // Calculate breathing metrics for this time range
        const timeRangeDuration = endTimeSeconds - startTimeSeconds;
        const breathingAnalysis = {
            timeRange: {
                start: startTimeSeconds,
                end: endTimeSeconds,
                duration: timeRangeDuration
            },
            totalBreathingPauses: breathingPauses.length,
            averageBreathDuration: breathingPauses.length > 0 ? 
                breathingPauses.reduce((sum, breath) => sum + breath.duration, 0) / breathingPauses.length : 0,
            breathingPauses: breathingPauses,
            breathingRate: this._calculateBreathingRateForTimeRange(breathingPauses, timeRangeDuration),
            recommendations: this._generateBreathingRecommendationsForTimeRange(breathingPauses, timeRangeDuration)
        };

        return breathingAnalysis;
    },

    /**
     * Calculate breathing rate for a specific time range
     * @private
     */
    _calculateBreathingRateForTimeRange : function(breathingPauses, timeRangeDurationSeconds) {
        if (breathingPauses.length === 0 || timeRangeDurationSeconds === 0) return 0;
        
        const breathsPerSecond = breathingPauses.length / timeRangeDurationSeconds;
        return Math.round(breathsPerSecond * 60 * 10) / 10; // Round to 1 decimal place
    },

    /**
     * Generate recommendations for breathing pause handling in a specific time range
     * @private
     */
    _generateBreathingRecommendationsForTimeRange : function(breathingPauses, timeRangeDurationSeconds) {
        const recommendations = [];
        
        if (breathingPauses.length === 0) {
            if (timeRangeDurationSeconds < 5) {
                recommendations.push({
                    type: 'short_segment',
                    message: 'Time range too short for meaningful breathing analysis'
                });
            } else {
                recommendations.push({
                    type: 'no_breathing_detected',
                    message: 'No breathing pauses detected in this time range - may be heavily processed audio'
                });
            }
            return recommendations;
        }

        // Analyze breathing patterns within this time range
        const longBreaths = breathingPauses.filter(b => b.duration > 800);
        const shortBreaths = breathingPauses.filter(b => b.duration < 400);
        
        if (longBreaths.length > breathingPauses.length * 0.3) {
            recommendations.push({
                type: 'long_breathing_detected',
                message: `${longBreaths.length} long breathing pauses detected in this segment - consider removal`,
                affectedTimestamps: longBreaths.map(b => b.timestamp)
            });
        }
        
        if (shortBreaths.length > breathingPauses.length * 0.5) {
            recommendations.push({
                type: 'natural_speech_rhythm',
                message: 'Natural speech rhythm detected in this segment'
            });
        }

        // Breathing density analysis
        const breathingDensity = breathingPauses.length / timeRangeDurationSeconds;
        if (breathingDensity > 0.3) { // More than 1 breath every 3 seconds
            recommendations.push({
                type: 'high_breathing_density',
                message: 'High breathing density - speaker may be nervous or audio may need processing'
            });
        } else if (breathingDensity < 0.1) { // Less than 1 breath every 10 seconds
            recommendations.push({
                type: 'low_breathing_density',
                message: 'Low breathing density - natural or well-edited speech'
            });
        }

        return recommendations;
    }

});

const BreathingAnalysisHelperInstance = new BreathingAnalysisHelper();

export default {
    getInstance: function () {
        return BreathingAnalysisHelperInstance;
    }
}; 